import { nonWhiteSpaceOr } from "@arti/utilities";
import { useCallback, useState } from "react";

import { useToast } from "../context/ToastContext";

interface UseCancellationOptions<T> {
  action: (item: T) => Promise<void>;
  messages: {
    confirmTitle: string;
    confirmBody: string | ((item: T) => string);
    successTitle?: string;
    successBody?: string | ((item: T) => string);
    errorTitle?: string;
    errorBody?: string | ((error: Error) => string);
  };
  onSuccess?: () => void;
}

function resolveErrorBody(
  errorBody: string | ((error: Error) => string) | undefined,
  error: unknown,
  fallback: string,
): string {
  if (!errorBody) {
    return fallback;
  }
  if (typeof errorBody === "function") {
    return errorBody(error instanceof Error ? error : new Error(fallback));
  }
  return errorBody;
}

export function useCancellation<T = void>({
  action,
  messages,
  onSuccess,
}: UseCancellationOptions<T>) {
  const [itemToCancel, setItemToCancel] = useState<T | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const requestCancellation = useCallback((item?: T) => {
    setItemToCancel(item ?? null);
    setError(null);
  }, []);

  const abortCancellation = useCallback(() => {
    setItemToCancel(null);
    setError(null);
  }, []);

  const confirmCancellation = useCallback(async () => {
    if (itemToCancel === null) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      await action(itemToCancel);
      toast.present({
        body:
          typeof messages.successBody === "function"
            ? messages.successBody(itemToCancel)
            : nonWhiteSpaceOr(messages.successBody, "Operation cancelled."),
        title: nonWhiteSpaceOr(messages.successTitle, "Cancellation requested"),
        tone: "warning",
      });
      setItemToCancel(null);
      onSuccess?.();
    } catch (_error) {
      const message =
        _error instanceof Error ? _error.message : "Unknown error";
      setError(message);
      const errorBody = resolveErrorBody(messages.errorBody, _error, message);
      toast.present({
        body: errorBody,
        title: nonWhiteSpaceOr(messages.errorTitle, "Cancellation failed"),
        tone: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [itemToCancel, action, messages, toast, onSuccess]);

  return {
    abortCancellation,
    confirmCancellation,
    error,
    isProcessing,
    itemToCancel,
    requestCancellation,
  };
}
